/**
 * Report Filter Card Component
 * Configurable filter card that composes individual filter fields
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Collapse,
  Divider,
  Typography,
} from '@mui/material'
import { ExpandMore, ExpandLess, FilterList as FiltersIcon } from '@mui/icons-material'
import { apiClient } from '../../../services/api'
import {
  ReportFilters,
  FilterKey,
  RegionOption,
  BdmOption,
  FilterLinkConfig,
  PRODUCT_STATUS_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  INVENTORY_TRACKING_OPTIONS,
  STOCK_OPTIONS,
  LAST_SOLD_AGING_OPTIONS,
  EXCEPTION_TYPE_OPTIONS,
  BUSINESS_AREA_OPTIONS,
} from './types'

// Import field components
import FranchisePartnerSearchField from './fields/FranchisePartnerSearchField'
import SearchField from './fields/SearchField'
import FPStatusField from './fields/FPStatusField'
import MarketField from './fields/MarketField'
import RegionField from './fields/RegionField'
import RomFbcField from './fields/RomFbcField'
import SystemStatusField from './fields/SystemStatusField'
import AdoptionStatusField from './fields/AdoptionStatusField'
import IcuStatusField from './fields/IcuStatusField'
import SellingStatusField from './fields/SellingStatusField'
import DateRangeField from './fields/DateRangeField'
import SystemField from './fields/SystemField'
import SyncStatusField from './fields/SyncStatusField'
import InitiatedByField from './fields/InitiatedByField'
import ConnectionStatusField from './fields/ConnectionStatusField'
import DataDomainField from './fields/DataDomainField'
import VisibleToRoleField from './fields/VisibleToRoleField'
import ProductSearchField from './fields/ProductSearchField'
import EnumDropdownField from './fields/EnumDropdownField'
import MainCategoryField from './fields/MainCategoryField'
import XeroAccountFilterField from './fields/XeroAccountFilterField'
import SupplierMultiSelectField from './fields/SupplierMultiSelectField'

interface ReportFilterCardProps {
  filters: ReportFilters
  enabledFilters: FilterKey[]
  onFilterChange: (key: keyof ReportFilters, value: string) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  loading?: boolean
  extraFilters?: React.ReactNode
  extraFiltersAfterFilter?: FilterKey
  filterLinkConfig?: FilterLinkConfig
  actionButton?: React.ReactNode
  /** Export control, rendered in the header row immediately left of Clear
   *  Filters (order: Export, then Clear Filters). Export lives here, never in
   *  the page header (RULES). Keep it outlined/secondary. */
  exportButton?: React.ReactNode
}

// Primary filters shown on first row
const PRIMARY_FILTERS: FilterKey[] = ['search', 'textSearch', 'fpStatus', 'market', 'region', 'system', 'initiatedBy', 'syncStatus', 'connectionStatus', 'dataDomain', 'exceptionType', 'businessArea', 'visibleToRole', 'supplierMultiSelect']

// Secondary filters shown in expandable section
const SECONDARY_FILTERS: FilterKey[] = ['romFbc', 'systemStatus', 'adoptionStatus', 'dateRange', 'icuStatus', 'sellingStatus']

// Product filters live in their own collapsible "Product Filters" section,
// sub-grouped into themed bands so the 9-field row is scannable. Order of
// the groups + keys defines render order. New fields slot into the
// matching band (Brand/Supplier -> Product info; Days-since-received ->
// Stock & Activity; Tax Rate/COGS -> Accounting) without changing call sites.
interface ProductFilterGroup {
  label: string
  keys: FilterKey[]
}

const PRODUCT_FILTER_GROUPS: ProductFilterGroup[] = [
  {
    label: 'Product info',
    keys: ['productSearch', 'productStatus', 'productType', 'mainCategory'],
  },
  {
    label: 'Stock & Activity',
    keys: ['inventoryTracking', 'stock', 'lastSoldAging'],
  },
  {
    label: 'Accounting',
    keys: ['xeroSalesAccount', 'xeroPurchaseAccount'],
  },
]

// Flat membership list - single derivation, no risk of drifting from groups.
const PRODUCT_FILTERS: FilterKey[] = PRODUCT_FILTER_GROUPS.flatMap(g => g.keys)

const ReportFilterCard: React.FC<ReportFilterCardProps> = ({
  filters,
  enabledFilters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  loading = false,
  extraFilters,
  extraFiltersAfterFilter,
  filterLinkConfig,
  actionButton,
  exportButton,
}) => {
  const enabledFiltersKey = enabledFilters.join(',')
  const [expanded, setExpanded] = useState(false)
  const [productExpanded, setProductExpanded] = useState(false)
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([])
  const [bdmOptions, setBdmOptions] = useState<BdmOption[]>([])
  const [initiatorOptions, setInitiatorOptions] = useState<string[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [loadingBdms, setLoadingBdms] = useState(false)
  const [loadingInitiators, setLoadingInitiators] = useState(false)

  // Determine which filters are enabled in each section
  const enabledPrimaryFilters = enabledFilters.filter((f) => PRIMARY_FILTERS.includes(f))
  const enabledSecondaryFilters = enabledFilters.filter((f) => SECONDARY_FILTERS.includes(f))
  const enabledProductFilters = enabledFilters.filter((f) => PRODUCT_FILTERS.includes(f))

  // Check if any secondary filters have values
  const hasSecondaryFilterValues = enabledSecondaryFilters.some((filterKey) => {
    if (filterKey === 'dateRange') {
      return filters.startDate || filters.endDate
    }
    return filters[filterKey as keyof ReportFilters]
  })

  // Show secondary filters when expanded or when they have values
  const showSecondaryFilters = expanded || hasSecondaryFilterValues

  // Auto-expand product filters when any have a non-default value
  const activeProductFilterCount = enabledProductFilters.filter((key) => {
    const v = filters[key as keyof ReportFilters]
    return v !== undefined && v !== ''
  }).length
  const showProductFilters = productExpanded || activeProductFilterCount > 0

  // Fetch regions when market changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (!filters.market || !enabledFilters.includes('region')) {
        setRegionOptions([])
        return
      }
      setLoadingRegions(true)
      try {
        const response = await apiClient.get(`/admin/regions?market=${filters.market}&per_page=100`)
        setRegionOptions(response.data.regions || [])
      } catch (error) {
        console.error('Failed to fetch regions:', error)
        setRegionOptions([])
      } finally {
        setLoadingRegions(false)
      }
    }
    fetchRegions()
  }, [filters.market, enabledFiltersKey])

  // Fetch BDMs on mount if romFbc filter is enabled
  useEffect(() => {
    const fetchBdms = async () => {
      if (!enabledFilters.includes('romFbc')) return
      setLoadingBdms(true)
      try {
        const response = await apiClient.get('/admin/bdm-options')
        setBdmOptions(response.data || [])
      } catch (error) {
        console.error('Failed to fetch BDMs:', error)
        setBdmOptions([])
      } finally {
        setLoadingBdms(false)
      }
    }
    fetchBdms()
  }, [enabledFiltersKey])

  // Fetch initiators on mount if initiatedBy filter is enabled
  useEffect(() => {
    const fetchInitiators = async () => {
      if (!enabledFilters.includes('initiatedBy')) return
      setLoadingInitiators(true)
      try {
        const response = await apiClient.get('/sync/initiators')
        setInitiatorOptions(response.data.initiators || [])
      } catch (error) {
        console.error('Failed to fetch initiators:', error)
        setInitiatorOptions([])
      } finally {
        setLoadingInitiators(false)
      }
    }
    fetchInitiators()
  }, [enabledFiltersKey])

  // Handle market change - clears dependent region
  const handleMarketChange = (value: string) => {
    onFilterChange('market', value)
    if (filters.region) {
      onFilterChange('region', '')
    }
  }

  // Render individual filter field
  const renderFilterField = (filterKey: FilterKey, gridSize: number = 3) => {
    if (!enabledFilters.includes(filterKey)) return null

    const gridProps = { item: true, xs: 12, md: gridSize }

    switch (filterKey) {
      case 'search':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <FranchisePartnerSearchField
              value={filters.search || ''}
              onChange={(v) => onFilterChange('search', v)}
              disabled={loading}
              statusFilter={filterLinkConfig?.linkFpStatusToSearch ? filters.fpStatus : undefined}
            />
          </Grid>
        )
      case 'fpStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <FPStatusField
              value={filters.fpStatus || ''}
              onChange={(v) => onFilterChange('fpStatus', v)}
            />
          </Grid>
        )
      case 'market':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <MarketField
              value={filters.market || ''}
              onChange={handleMarketChange}
            />
          </Grid>
        )
      case 'region':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <RegionField
              value={filters.region || ''}
              onChange={(v) => onFilterChange('region', v)}
              regionOptions={regionOptions}
              disabled={!filters.market}
              loading={loadingRegions}
            />
          </Grid>
        )
      case 'romFbc':
        return (
          <Grid {...gridProps} key={filterKey}>
            <RomFbcField
              value={filters.romFbc || ''}
              onChange={(v) => onFilterChange('romFbc', v)}
              bdmOptions={bdmOptions}
              loading={loadingBdms}
            />
          </Grid>
        )
      case 'systemStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <SystemStatusField
              value={filters.systemStatus || ''}
              onChange={(v) => onFilterChange('systemStatus', v)}
            />
          </Grid>
        )
      case 'adoptionStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <AdoptionStatusField
              value={filters.adoptionStatus || ''}
              onChange={(v) => onFilterChange('adoptionStatus', v)}
            />
          </Grid>
        )
      case 'dateRange':
        return (
          <Grid {...gridProps} md={6} key={filterKey}>
            <DateRangeField
              startDate={filters.startDate || ''}
              endDate={filters.endDate || ''}
              onStartDateChange={(v) => onFilterChange('startDate', v)}
              onEndDateChange={(v) => onFilterChange('endDate', v)}
            />
          </Grid>
        )
      case 'system':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <SystemField
              value={filters.system || ''}
              onChange={(v) => onFilterChange('system', v)}
            />
          </Grid>
        )
      case 'syncStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <SyncStatusField
              value={filters.syncStatus || ''}
              onChange={(v) => onFilterChange('syncStatus', v)}
            />
          </Grid>
        )
      case 'initiatedBy':
        return (
          <Grid {...gridProps} md={3} key={filterKey}>
            <InitiatedByField
              value={filters.initiatedBy || ''}
              onChange={(v) => onFilterChange('initiatedBy', v)}
              initiatorOptions={initiatorOptions}
              loading={loadingInitiators}
            />
          </Grid>
        )
      case 'textSearch':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <SearchField
              value={filters.textSearch || ''}
              onChange={(v) => onFilterChange('textSearch', v)}
              label="Search"
            />
          </Grid>
        )
      case 'icuStatus':
        return (
          <Grid {...gridProps} md={1} key={filterKey}>
            <IcuStatusField
              value={filters.icuStatus || ''}
              onChange={(v) => onFilterChange('icuStatus', v)}
            />
          </Grid>
        )
      case 'sellingStatus':
        return (
          <Grid {...gridProps} md={1} key={filterKey}>
            <SellingStatusField
              value={filters.sellingStatus || ''}
              onChange={(v) => onFilterChange('sellingStatus', v)}
            />
          </Grid>
        )
      case 'connectionStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <ConnectionStatusField
              value={filters.connectionStatus || ''}
              onChange={(v) => onFilterChange('connectionStatus', v)}
            />
          </Grid>
        )
      case 'dataDomain':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <DataDomainField
              value={filters.dataDomain || ''}
              onChange={(v) => onFilterChange('dataDomain', v)}
            />
          </Grid>
        )
      case 'exceptionType':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Exception Type"
              value={filters.exceptionType || ''}
              options={EXCEPTION_TYPE_OPTIONS}
              onChange={(v) => onFilterChange('exceptionType', v)}
            />
          </Grid>
        )
      case 'businessArea':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Business Area"
              value={filters.businessArea || ''}
              options={BUSINESS_AREA_OPTIONS}
              onChange={(v) => onFilterChange('businessArea', v)}
            />
          </Grid>
        )
      case 'visibleToRole':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <VisibleToRoleField
              value={filters.visibleToRole || ''}
              onChange={(v) => onFilterChange('visibleToRole', v)}
            />
          </Grid>
        )
      case 'productSearch':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <ProductSearchField
              value={filters.productSearch || ''}
              onChange={(v) => onFilterChange('productSearch', v)}
            />
          </Grid>
        )
      case 'productStatus':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Product Status"
              value={filters.productStatus || ''}
              options={PRODUCT_STATUS_OPTIONS}
              onChange={(v) => onFilterChange('productStatus', v)}
            />
          </Grid>
        )
      case 'productType':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Product Type"
              value={filters.productType || ''}
              options={PRODUCT_TYPE_OPTIONS}
              onChange={(v) => onFilterChange('productType', v)}
            />
          </Grid>
        )
      case 'mainCategory':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <MainCategoryField
              value={filters.mainCategory || ''}
              onChange={(v) => onFilterChange('mainCategory', v)}
            />
          </Grid>
        )
      case 'inventoryTracking':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Inventory Tracking"
              value={filters.inventoryTracking || ''}
              options={INVENTORY_TRACKING_OPTIONS}
              onChange={(v) => onFilterChange('inventoryTracking', v)}
            />
          </Grid>
        )
      case 'stock':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Stock"
              value={filters.stock || ''}
              options={STOCK_OPTIONS}
              onChange={(v) => onFilterChange('stock', v)}
            />
          </Grid>
        )
      case 'lastSoldAging':
        return (
          <Grid {...gridProps} md={2} key={filterKey}>
            <EnumDropdownField
              label="Last Sold"
              value={filters.lastSoldAging || ''}
              options={LAST_SOLD_AGING_OPTIONS}
              onChange={(v) => onFilterChange('lastSoldAging', v)}
            />
          </Grid>
        )
      case 'xeroSalesAccount':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <XeroAccountFilterField
              label="Xero Sales Account"
              state={filters.xeroSalesAccount || ''}
              code={filters.xeroSalesAccountCode || ''}
              onStateChange={(v) => onFilterChange('xeroSalesAccount', v)}
              onCodeChange={(v) => onFilterChange('xeroSalesAccountCode', v)}
              kind="sales"
              market={filters.market}
            />
          </Grid>
        )
      case 'xeroPurchaseAccount':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <XeroAccountFilterField
              label="Xero Purchase Account"
              state={filters.xeroPurchaseAccount || ''}
              code={filters.xeroPurchaseAccountCode || ''}
              onStateChange={(v) => onFilterChange('xeroPurchaseAccount', v)}
              onCodeChange={(v) => onFilterChange('xeroPurchaseAccountCode', v)}
              kind="purchase"
              market={filters.market}
            />
          </Grid>
        )
      case 'supplierMultiSelect':
        return (
          <Grid {...gridProps} md={4} key={filterKey}>
            <SupplierMultiSelectField
              value={filters.supplierMultiSelect || ''}
              onChange={(v) => onFilterChange('supplierMultiSelect', v)}
              market={filters.market}
              disabled={loading}
            />
          </Grid>
        )
      default:
        return null
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FiltersIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {exportButton}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                onClearFilters()
                setExpanded(false)
              }}
              disabled={!hasActiveFilters || loading}
              sx={{
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'text.secondary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
        {/* Primary Filters Row */}
        <Grid container spacing={2} alignItems="flex-end">
          {enabledPrimaryFilters.map((filterKey) => (
            <React.Fragment key={filterKey}>
              {renderFilterField(filterKey)}
              {extraFilters && extraFiltersAfterFilter === filterKey && (
                <Grid item xs={12} md="auto">
                  {extraFilters}
                </Grid>
              )}
            </React.Fragment>
          ))}
          {extraFilters && !extraFiltersAfterFilter && (
            <Grid item xs={12} md="auto">
              {extraFilters}
            </Grid>
          )}
        </Grid>

        {/* Secondary Filters Row (collapsible) */}
        {enabledSecondaryFilters.length > 0 && (
          <Collapse in={showSecondaryFilters}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {enabledSecondaryFilters.map((filterKey) => renderFilterField(filterKey))}
            </Grid>
          </Collapse>
        )}

        {/* Product Filters Row (collapsible) - separate from secondary so
            the FP row stays clean for catalogue reports */}
        {enabledProductFilters.length > 0 && (
          <>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Button
                size="small"
                onClick={() => setProductExpanded(!productExpanded)}
                startIcon={showProductFilters ? <ExpandLess /> : <ExpandMore />}
                sx={{ color: 'text.secondary' }}
              >
                Product Filters
                {activeProductFilterCount > 0 && ` (${activeProductFilterCount} active)`}
              </Button>
            </Box>
            <Collapse in={showProductFilters}>
              <Box sx={{ mt: 0.5 }}>
                {PRODUCT_FILTER_GROUPS.map((group, idx) => {
                  const groupKeys = group.keys.filter((k) => enabledFilters.includes(k))
                  if (groupKeys.length === 0) return null
                  return (
                    <Box key={group.label} sx={{ mt: idx === 0 ? 0 : 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant="overline"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            mr: 1.5,
                            lineHeight: 1.5,
                          }}
                        >
                          {group.label}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                      <Grid container spacing={2}>
                        {groupKeys.map((filterKey) => renderFilterField(filterKey))}
                      </Grid>
                    </Box>
                  )
                })}
              </Box>
            </Collapse>
          </>
        )}

        {/* Action Row: More/Less Filters + Action Button */}
        {(enabledSecondaryFilters.length > 0 || actionButton) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            {/* Left: More/Less toggle (FP-level secondary filters) */}
            <Box>
              {enabledSecondaryFilters.length > 0 && !hasSecondaryFilterValues && (
                <Button
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                  sx={{ color: 'text.secondary' }}
                >
                  {expanded ? 'Less Filters' : 'More Filters'}
                </Button>
              )}
            </Box>
            {/* Right: Action button */}
            {actionButton && <Box>{actionButton}</Box>}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ReportFilterCard
